// useDayCloseAPI.ts - 純 API 方法提供者
import { useCallback } from 'react';
import {
    startDayClose,
    getTodayDayClose,
    getDayCloseById,
    saveTimeBoxProcessing,
    saveReflection,
    completeDayClose
} from '../services/api/dayClose';
import { DayClose } from '../types/dayClose';
import { getTimeBoxesByDate } from '../services/api/timeBoxes';
import { TimeBox } from '../types/timeBox';
import { eventService } from '../services/api/calendar-events';
import { Event } from '../types/calendar-event';
import { ScheduleItem, sortScheduleItems } from '../types/schedule';
import { taskService } from '../features/task';
import { getCurrentUserId } from '../services/auth';
import { TaskPriority, TaskEnergyLevel, TaskCategory } from '../types/task';
import { getCaptureById, getCapturesByIds } from '../services/api/captures';
import { Capture } from '../types/capture';
import { logWarn, logError } from '../utils/logger';

export const useDayCloseAPI = () => {
    // 檢查今天的 Day Close
    const checkTodayDayClose = useCallback(async (userId: string): Promise<DayClose | null> => {
        if (!userId) return null;
        try {
            return await getTodayDayClose(userId);
        } catch (error) {
            throw new Error('Failed to check today Day Close status');
        }
    }, []);

    // 創建新的 Day Close
    const createDayClose = useCallback(async (userId: string): Promise<string> => {
        if (!userId) throw new Error('User ID is required');
        
        try {
            // 先檢查是否已存在
            const existing = await getTodayDayClose(userId);
            if (existing) {
                return existing.id;
            }
            
            // 創建新的
            return await startDayClose(userId);
        } catch (error) {
            throw new Error('Failed to create Day Close');
        }
    }, []);

    // 載入 Day Close 詳細資料
    const loadDayClose = useCallback(async (dayCloseId: string): Promise<DayClose | null> => {
        try {
            return await getDayCloseById(dayCloseId);
        } catch (error) {
            throw new Error('Failed to load Day Close data');
        }
    }, []);

    // 載入今天的 TimeBoxes
    const loadTodayTimeBoxes = useCallback(async (userId: string): Promise<TimeBox[]> => {
        try {
            const today = new Date();
            return await getTimeBoxesByDate(today, userId);
        } catch (error) {
            throw new Error('Failed to load TimeBoxes');
        }
    }, []);

    // 載入今天的 Events
    const loadTodayEvents = useCallback(async (userId: string): Promise<Event[]> => {
        try {
            const today = new Date();
            return await eventService.getEventsByDate(userId, today);
        } catch (error) {
            throw new Error('Failed to load Events');
        }
    }, []);

    // 載入今天的所有排程項目 (TimeBoxes + Events)
    const loadTodayScheduleItems = useCallback(async (userId: string): Promise<ScheduleItem[]> => {
        try {
            const [timeBoxes, events] = await Promise.all([
                loadTodayTimeBoxes(userId),
                loadTodayEvents(userId)
            ]);
            
            // 合併並排序排程項目
            const scheduleItems: ScheduleItem[] = sortScheduleItems([
                ...timeBoxes,
                ...events
            ]);
            
            return scheduleItems;
        } catch (error) {
            throw new Error('Failed to load schedule items');
        }
    }, [loadTodayTimeBoxes, loadTodayEvents]);

    // 轉換 TimeBox 為 Task
    const createTasksFromTimeBoxes = useCallback(async (
        timeBoxesToConvert: TimeBox[],
        actions: DayClose['incompleteActions']
    ): Promise<{
        successCount: number;
        failedCount: number;
        failedTimeBoxes: string[];
    }> => {
        const currentUserId = getCurrentUserId();
        if (!currentUserId) {
            throw new Error('User not authenticated');
        }

        let successCount = 0;
        let failedCount = 0;
        const failedTimeBoxes: string[] = [];

        const timeBoxesToProcess = timeBoxesToConvert.filter(timeBox => {
            const action = actions.find(a => a.timeBoxId === timeBox.id);
            return action?.action === 'add_todo';
        });

        if (timeBoxesToProcess.length === 0) return { successCount, failedCount, failedTimeBoxes };

        const createPromises = timeBoxesToProcess.map(async (timeBox) => {
            try {
                // 映射 TimeBox category 到 TaskCategory (只包含 TaskCategory 的合法值)
                const categoryMap: Record<string, TaskCategory | undefined> = {
                    'focus': 'work',
                    'routine': 'personal',
                    'life': 'personal',
                    'rest': undefined
                };

                const mappedCategory = categoryMap[timeBox.category];

                const taskData = {
                    title: timeBox.title,
                    notes: timeBox.notes || timeBox.description || `Converted from time box: ${timeBox.title}`,
                    ...(mappedCategory && { category: mappedCategory }),
                    priority: '2' as TaskPriority,
                    energyLevel: '2' as TaskEnergyLevel,
                    isQuickTask: false,
                    userId: currentUserId,
                    location: timeBox.location
                };

                await taskService.createTask(taskData);
                return { success: true, timeBoxId: timeBox.id };
            } catch (error) {
                return { success: false, timeBoxId: timeBox.id, error };
            }
        });

        const results = await Promise.allSettled(createPromises);

        results.forEach((result, index) => {
            if (result.status === 'fulfilled') {
                if (result.value.success) {
                    successCount++;
                } else {
                    failedCount++;
                    failedTimeBoxes.push(result.value.timeBoxId);
                }
            } else {
                failedCount++;
                failedTimeBoxes.push(timeBoxesToProcess[index].id);
            }
        });

        return { successCount, failedCount, failedTimeBoxes };
    }, []);

    // 保存統一的排程項目處理結果
    const saveScheduleItemProcessingResult = useCallback(async (
        dayCloseId: string,
        completedTimeBoxes: string[],
        incompleteTimeBoxes: string[],
        completedEvents: string[],
        incompleteEvents: string[],
        actions: DayClose['incompleteActions'],
        todayScheduleItems: ScheduleItem[],
        timeBoxCaptureIds?: string[],
        eventCaptureIds?: string[]
    ): Promise<void> => {
        try {
            // 調用後端 API 儲存混合的排程項目處理結果
            // 注意：需要更新後端 API 來支援 Events
            await saveTimeBoxProcessing(
                dayCloseId, 
                completedTimeBoxes, 
                incompleteTimeBoxes, 
                actions, 
                timeBoxCaptureIds
            );
            
            // TODO: 添加 Event 處理結果的儲存
            // 目前只儲存 TimeBox 相關數據，Events 支援需要後端 API 更新

            // 處理任務轉換（非阻塞）- 只針對 TimeBoxes
            const hasTaskActions = actions.some(action => action.action === 'add_todo' && (!action.itemType || action.itemType === 'timeBox'));
            if (hasTaskActions && todayScheduleItems.length > 0) {
                try {
                    const todayTimeBoxes = todayScheduleItems.filter(item => item.itemType === 'timebox') as TimeBox[];
                    await createTasksFromTimeBoxes(todayTimeBoxes, actions);
                } catch (taskError) {
                    // 任務轉換失敗不影響主流程
                    logWarn('Task conversion failed:', taskError);
                }
            }
        } catch (error) {
            throw new Error('Failed to save schedule item processing');
        }
    }, [createTasksFromTimeBoxes]);

    // 保存 TimeBox 處理結果 (向後兼容)
    const saveTimeBoxProcessingResult = useCallback(async (
        dayCloseId: string,
        completedBoxes: string[],
        incompleteBoxes: string[],
        actions: DayClose['incompleteActions'],
        todayTimeBoxes: TimeBox[],
        timeBoxCaptureIds?: string[]
    ): Promise<void> => {
        try {
            await saveTimeBoxProcessing(dayCloseId, completedBoxes, incompleteBoxes, actions, timeBoxCaptureIds);

            // 處理任務轉換（非阻塞）
            const hasTaskActions = actions.some(action => action.action === 'add_todo');
            if (hasTaskActions && todayTimeBoxes.length > 0) {
                try {
                    await createTasksFromTimeBoxes(todayTimeBoxes, actions);
                } catch (taskError) {
                    // 任務轉換失敗不影響主流程
                }
            }
        } catch (error) {
            throw new Error('Failed to save timeBox processing');
        }
    }, [createTasksFromTimeBoxes]);

    // 保存反思內容
    const saveReflectionContent = useCallback(async (dayCloseId: string, captureId: string): Promise<void> => {
        try {
            await saveReflection(dayCloseId, captureId);
        } catch (error) {
            throw new Error('Failed to save reflection');
        }
    }, []);

    // 完成 Day Close
    const finishDayClose = useCallback(async (dayCloseId: string, status: 'completed' | 'partial' = 'completed'): Promise<void> => {
        try {
            await completeDayClose(dayCloseId, status);
        } catch (error) {
            throw new Error('Failed to complete Day Close');
        }
    }, []);

    // 載入 Reflection 內容
    const loadReflectionContent = useCallback(async (captureId: string): Promise<Capture | null> => {
        try {
            return await getCaptureById(captureId);
        } catch (error) {
            logError('Failed to load reflection content:', error);
            return null;
        }
    }, []);

    // 載入多個 TimeBox Captures
    const loadTimeBoxCaptures = useCallback(async (captureIds: string[]): Promise<Capture[]> => {
        try {
            return await getCapturesByIds(captureIds);
        } catch (error) {
            logError('Failed to load timeBox captures:', error);
            return [];
        }
    }, []);

    // 載入多個 Event Captures
    const loadEventCaptures = useCallback(async (captureIds: string[]): Promise<Capture[]> => {
        try {
            return await getCapturesByIds(captureIds);
        } catch (error) {
            logError('Failed to load event captures:', error);
            return [];
        }
    }, []);

    // 載入明天的 TimeBoxes
    const loadTomorrowTimeBoxes = useCallback(async (userId: string): Promise<TimeBox[]> => {
        try {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            return await getTimeBoxesByDate(tomorrow, userId);
        } catch (error) {
            throw new Error('Failed to load tomorrow TimeBoxes');
        }
    }, []);

    // 載入明天的 Events
    const loadTomorrowEvents = useCallback(async (userId: string): Promise<Event[]> => {
        try {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            return await eventService.getEventsByDate(userId, tomorrow);
        } catch (error) {
            throw new Error('Failed to load tomorrow Events');
        }
    }, []);

    // 載入明天的所有排程項目
    const loadTomorrowScheduleItems = useCallback(async (userId: string): Promise<ScheduleItem[]> => {
        try {
            const [timeBoxes, events] = await Promise.all([
                loadTomorrowTimeBoxes(userId),
                loadTomorrowEvents(userId)
            ]);
            
            // 合併並排序排程項目
            const scheduleItems: ScheduleItem[] = sortScheduleItems([
                ...timeBoxes,
                ...events
            ]);
            
            return scheduleItems;
        } catch (error) {
            throw new Error('Failed to load tomorrow schedule items');
        }
    }, [loadTomorrowTimeBoxes, loadTomorrowEvents]);

    return {
        // Day Close 基本操作
        checkTodayDayClose,
        createDayClose,
        loadDayClose,
        finishDayClose,
        
        // 資料載入 - TimeBoxes
        loadTodayTimeBoxes,
        loadTomorrowTimeBoxes,
        
        // 資料載入 - Events
        loadTodayEvents,
        loadTomorrowEvents,
        
        // 資料載入 - 統一排程項目
        loadTodayScheduleItems,
        loadTomorrowScheduleItems,
        
        // 儲存處理結果
        saveTimeBoxProcessingResult, // 向後兼容
        saveScheduleItemProcessingResult, // 新的統一方法
        
        // 反思和 Capture 相關
        saveReflectionContent,
        loadReflectionContent,
        loadTimeBoxCaptures,
        loadEventCaptures
    };
};